import { computeEdge } from "./edge.js";
import { decisionDeadline } from "./marketAge.js";

export const SORT_OPTIONS = ["edge", "value", "time", "spread"];

// One edge per game, keyed on the game object. The per-book edge model is the
// expensive part of Home's render; computing it inside a sort comparator ran
// it twice per comparison, so everything that needs an edge reads this map.
export function edgeMap(games, compute = computeEdge) {
  const edges = new Map();
  for (const game of games) {
    if (!edges.has(game)) edges.set(game, compute(game));
  }
  return edges;
}

// Plain substring match, not fuzzy: team names are short, unambiguous
// tokens, and Fuse's edit-distance fuzziness let a query like "lion"
// match "Falcons" (1 substitution from the "lcon" substring) and
// "Steelers" — exactly the false positives a team search must avoid.
//
// Returns a new array; an empty query matches every game.
export function searchGames(games, rawQuery) {
  if (!rawQuery) return [...games];
  const query = rawQuery.trim().toLowerCase();
  const teamMatches = (team) =>
    [team.name, team.abbr, team.shortName].some((field) =>
      field?.toLowerCase().includes(query)
    );
  return games.filter(
    (game) => teamMatches(game.homeTeam) || teamMatches(game.awayTeam)
  );
}

// Returns a new array and never reorders its input. Ties keep their input
// order (Array.prototype.sort is stable).
//
// `edges` is an `edgeMap` of these games; a game missing from it is computed
// on the spot, once.
export function sortGames(games, option, edges = new Map()) {
  const sorted = [...games];

  if (option === "time") {
    return sorted.sort((a, b) => new Date(a.kickoffAt) - new Date(b.kickoffAt));
  }
  if (option === "spread") {
    return sorted.sort((a, b) => (b.std || 0) - (a.std || 0));
  }

  // Games with no frozen line have no measurable edge; they sort last
  // rather than sorting as zero, which would scatter them mid-list.
  //
  // The comparison is on `winProbability` — the chance this pick actually
  // wins — rather than on `points`. Ranking on points treats a point as a
  // point, which in the NFL it is not.
  const missing = sorted.filter((game) => !edges.has(game));
  const computed = edgeMap(missing);
  const edgeOf = (game) => edges.get(game) ?? computed.get(game);
  // "value" orders on stale-line value instead: how much of a game's win
  // probability came from holding a number the books no longer post. A view,
  // not the recommendation -- the five are still the top win probabilities.
  const key = option === "value" ? "lineValueProbability" : "winProbability";
  return sorted.sort((a, b) => {
    const ea = edgeOf(a);
    const eb = edgeOf(b);
    if (!ea.measurable && !eb.measurable) return 0;
    if (!ea.measurable) return 1;
    if (!eb.measurable) return -1;
    return eb[key] - ea[key];
  });
}

// The game Home's "Best pick" badge names: the highest outright win
// probability among games still open to a decision. Finished games are out,
// and so is any game past its decision deadline (`decisionDeadline`, the
// earlier of kickoff and Sunday 1:00pm ET, or the server's cutoff once it has
// passed): a game in progress used to be named here, because only `completed`
// was filtered and nothing marks a game completed until the score sync runs.
// `isRanked` is Home's own rule for which games compete for the five.
export function bestPick(games, edges = new Map(), now = Date.now(), isRanked = () => true) {
  const missing = games.filter((game) => !edges.has(game));
  const computed = edgeMap(missing);
  const edgeOf = (game) => edges.get(game) ?? computed.get(game);

  return (
    games
      .map((game) => ({ game, edge: edgeOf(game) }))
      .filter(({ game, edge }) => {
        if (game.completed || !isRanked(game) || !edge.measurable) return false;
        const deadline = decisionDeadline(game);
        return deadline == null || now < deadline;
      })
      .sort((a, b) => b.edge.winProbability - a.edge.winProbability)[0] ?? null
  );
}
